import type { HubProgressBar as _HubProgressBar } from './HubProgressBar.types';
import type { HubOptionScore as _HubOptionScore } from './HubOptionScore.types';
import type { ReplayDefaults as _ReplayDefaults, ReplayCondition as _ReplayCondition } from './HubOptionReplay.types';
import type { StateCondition as _StateCondition } from './HubOptionState.types';
import type { TextDestCondition as _TextDestCondition } from './HubOptionTextDest.types';
import type { IconCondition as _IconCondition } from './HubOptionIcon.types';
import type { TagCondition as _TagCondition, HubOptionTag as _HubOptionTag } from './HubOptionTag.types';
import type { BorderCondition as _BorderCondition } from './HubOptionBorder.types';

export type {
  SubCondition,
  StateCondition,
  HubConditionOperator,
} from './HubOptionState.types';

export {
  HubOptionStateValue,
  ConditionQuantifier,
  HUB_MAX_STATE_CONDITIONS,
  HUB_MAX_SUB_CONDITIONS,
} from './HubOptionState.types';

export type { ReplayDefaults, ReplayCondition } from './HubOptionReplay.types';
export { HUB_MAX_REPLAY_CONDITIONS } from './HubOptionReplay.types';

export type { TextDestCondition } from './HubOptionTextDest.types';
export { HUB_MAX_TEXT_DEST_CONDITIONS } from './HubOptionTextDest.types';

export type { IconCondition } from './HubOptionIcon.types';
export { HUB_MAX_ICON_CONDITIONS } from './HubOptionIcon.types';

export type { TagCondition, HubOptionTag } from './HubOptionTag.types';
export { HUB_MAX_TAG_CONDITIONS, HUB_MAX_DEFAULT_TAGS } from './HubOptionTag.types';

export type { BorderCondition } from './HubOptionBorder.types';
export { HUB_MAX_BORDER_CONDITIONS } from './HubOptionBorder.types';

export type { Mod, ModRule, HubGotoProp } from './HubMod.types';
export { HUB_GOTO_PROPS, isHubGotoProp } from './HubMod.types';

export type { HubTagTemplate } from './HubTagTemplate';

export type { HubProgressBar } from './HubProgressBar.types';

export type { HubOptionScore, HubScoreValueType } from './HubOptionScore.types';

export type HubSettings = {
  id?: number;
  stepId?: number;
  header: { text: string };
  subheader: { text: string } | null;
  progressBars?: _HubProgressBar[];
};

export type HubOptionState = {
  state: 'active' | 'hidden' | 'disabled';
  text?: string;
};

export type HubOption = {
  id?: number;
  stepId?: number;
  uuid: string;
  sortOrder: number;
  viewType: 'card' | 'button';
  title: string;
  gotoBranchId: number | null;
  iconId: number | null;
  borderColor: string | null;
  state: HubOptionState;
  score: _HubOptionScore | null;
  replay: _ReplayDefaults | null;
  mods: unknown[];
  /** State conditions, evaluated top-to-bottom, first-match-wins. Frontend-only until mods integration. */
  stateConditions?: _StateCondition[];
  /** Replay conditions, evaluated top-to-bottom, first-match-wins. Frontend-only until mods integration. */
  replayConditions?: _ReplayCondition[];
  /** Title & Destination conditions, evaluated top-to-bottom, first-match-wins. Frontend-only until mods integration. */
  textDestConditions?: _TextDestCondition[];
  /** Icon conditions, evaluated top-to-bottom, first-match-wins. Frontend-only until mods integration. */
  iconConditions?: _IconCondition[];
  /** Tag override conditions for top position. Frontend-only — serialized to mods on save. */
  tagTopConditions?: _TagCondition[];
  /** Tag override conditions for bottom position. Frontend-only — serialized to mods on save. */
  tagBottomConditions?: _TagCondition[];
  /** Default tags — { top: [...], bottom: [...] }. Stored as JSON column on HubOptions. */
  tags?: { top: _HubOptionTag[]; bottom: _HubOptionTag[] };
  /** Border color conditions. Frontend-only — serialized to mods on save. */
  borderConditions?: _BorderCondition[];
};

export const isContinueOption = (o: HubOption): boolean => o.viewType === 'button';

export const HUB_MAX_REGULAR_OPTIONS = 15;
export const HUB_CONTINUE_SORT_ORDER = 9999;
